import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Row,
  Col,
  Spin,
  Tabs,
  Form,
  Card,
  Input,
  DatePicker,
  Checkbox,
  Button,
  message,
} from 'antd';
import {
  CalculatorOutlined,
  UnorderedListOutlined,
  DollarOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import CreateClave from 'utils/createClave';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Table from 'components/Table';
import NumericInput from 'components/NumericInput';
import Editor from 'components/Editor/index';
import Select from 'components/Select';
import { createDecimalRegExp } from 'utils/patterns';
import { onError } from 'utils/handlers';
import PlainTransfer from 'components/PlainTransfer';
import { getCatalogoDeTarifas } from 'api/configuracion/catalogoDeTarifas';
import { getCatalogoDePadrones } from 'api/catalogos';
import { getCanalesDePago } from 'api/configuracion/canalDePago';
import {
  getTiposDeAplicacionDeDescuentos,
  getTiposDeAplicacion,
} from 'api/recaudacion/index';
import { getVariables } from 'api/configuracion/variables';
import {
  baseURI,
  deleteTipoDeDescuento,
  getTipoDeDescuento,
  getTiposDeDescuentos,
  patchTipoDeDescuento,
  postTipoDeDescuento,
} from 'api/recaudacion/tiposDeDescuento';
import { hasActionPermission, ActionsNames } from 'utils/permission/index';
import { capitalize } from 'utils/normalizers';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import Moment from 'moment';
import { getGrupos } from 'api/catalogos/grupos';

const { TabPane } = Tabs;

const catalogsModels = [
  'aplicaciondeldescuento',
  'catalogodecanalesdepago',
  'catalogodetarifa',
  'padron',
  'tipodeaplicacionparacargo',
  'variable',
];

// eslint-disable-next-line react/prop-types
const TiposDeDescuentos = ({ permission }) => {
  const monedaDefault = useSelector(({ auth }) => auth.monedaDefault);
  const [decimal] = createDecimalRegExp(monedaDefault);
  const padrones = useSelector(({ catalogs }) => catalogs.padrones);
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const tiposDeAplicacionDescuentos = useSelector(({ catalogs }) => catalogs
    .tiposDeAplicacionDescuentos);
  const [form] = Form.useForm();
  const [formCalculo] = Form.useForm();
  const [formReglas] = Form.useForm();
  const [formCanalesDePago] = Form.useForm();
  const es_descuento_especial = Form.useWatch('es_descuento_especial', formReglas);
  const requiere_autorizacion = Form.useWatch('requiere_autorizacion', formReglas);
  const tiposDeAplicacion = useSelector(({ catalogs }) => catalogs.tiposDeAplicacion)
    .filter(({ id }) => ![2, 4].includes(id))
    .map((e) => ({
      ...e,
      disabled: /variable|capturable/.test(e.descripcion.toLowerCase()) && !es_descuento_especial,
    }));
  const [formVariables] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const [required, setRequired] = useState(false);
  const [tipoApp, setTipoApp] = useState([]);
  const [canalesDePago, setCanalesDePago] = useState([]);
  const [tarifas, setTarifas] = useState([]);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [porDiasDeVigencia, setPorDiasDeVigencia] = useState(false);
  const [variables, setVariables] = useState([]);
  const [roles, setRoles] = useState([]);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const esPorNumeroDeAplicacion = Form.useWatch('es_por_numero_de_aplicaciones', formCalculo);

  const formInstances = [
    form,
    formCalculo,
    formReglas,
    formCanalesDePago,
    formVariables,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getTiposDeDescuentos();
    setData(_data);
    setLoading(false);
  };

  // region Fetch Data
  useEffect(() => {
    let mounted = true;
    const validatePermission = () => {
      const _hasAllViewPermission = catalogsModels
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const [
        dataVariables,
        dataCanalesDePago,
        dataCatalogosDeTarifas,
        _roles,
      ] = await Promise.all([
        getVariables({ es_script: true }),
        getCanalesDePago(),
        getCatalogoDeTarifas(),
        getGrupos(),
        getCatalogoDePadrones(),
        getTiposDeAplicacion(true),
        getTiposDeAplicacionDeDescuentos(),
      ]);

      if (mounted) {
        setTarifas(dataCatalogosDeTarifas);
        setCanalesDePago(dataCanalesDePago);
        setVariables(dataVariables);
        setRoles(_roles);
      }

      await fetchData();
      setLoading(false);
    };
    fetchAll();
    validatePermission();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setPorDiasDeVigencia(false);
    setRequired(false);
    setTipoApp(1);
    setSelectedRowKeys([]);
    setCurrentTabKey('general');
    form.resetFields();
    formCalculo.resetFields();
    formReglas.resetFields();
    formCanalesDePago.resetFields();
    formVariables.resetFields();
  };

  // region onFinish
  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      await formCalculo.validateFields();
      await formReglas.validateFields();
      await formCanalesDePago.validateFields();
      await formVariables.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formCalculo.getFieldsValue(),
        ...formReglas.getFieldsValue(),
        ...formCanalesDePago.getFieldsValue(),
        ...formVariables.getFieldsValue(),
      };
      if (es_descuento_especial) values.aplica_por_periodo = false;
      const [key] = selectedRowKeys;
      const saved = await (key ? patchTipoDeDescuento(key, values) : postTipoDeDescuento(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances, setCurrentTabKey);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteTipoDeDescuento(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
    CreateClave(data, form);
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const record = await getTipoDeDescuento(key);
    setTipoApp(record.tipo_de_aplicacion);
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
    formCalculo.setFieldsValue({
      ...record,
      es_por_dias_de_vigencia: !!record.dias_de_vigencia,
    });
    formReglas.setFieldsValue(record);
    formCanalesDePago.setFieldsValue(record);
    formVariables.setFieldsValue(record);
    setPorDiasDeVigencia(!!record.dias_de_vigencia);
    setRequired(record.el_descuento_es_por_rango_de_fechas);
    setLoading(false);
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  // region Columns
  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 100,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 250,
    },
    {
      titleText: 'Tipo de Aplicación',
      dataIndex: 'tipo_de_aplicacion',
      key: 'tipo_de_aplicacion',
      width: 200,
      render: (val) => (tiposDeAplicacion.find((item) => item.id === val)?.descripcion),
    },
    {
      titleText: 'Rango de Fechas',
      dataIndex: 'el_descuento_es_por_rango_de_fechas',
      key: 'el_descuento_es_por_rango_de_fechas',
      width: 250,
      render: (val, { fecha_inicio, fecha_fin }) => (val
        ? `${fecha_inicio?.format('DD/MM/YYYY')} - ${fecha_fin?.format('DD/MM/YYYY')}` : 'N/A'),
    },
  ];

  // region Rules
  const requiredRule = {
    required: true,
  };

  const percentajeValidator = {
    validator: async (rule, val) => {
      const parsedVal = parseFloat(val);
      if (val) {
        if (!decimal.test(val)) {
          throw new Error('Ingrese un valor decimal válido');
        } else if (parsedVal > 100) {
          throw new Error('El valor no puede ser mayor a 100');
        } else if (parsedVal <= 0) {
          throw new Error('El valor debe ser mayor a 0');
        }
      }
    },
  };

  const rules = {
    required: [requiredRule],
    porcentaje: [
      requiredRule,
      percentajeValidator,
    ],
    importe: [
      requiredRule,
      {
        validator: async (rule, val) => {
          if (val && !decimal.test(val)) {
            throw new Error('Ingrese un valor decimal válido');
          }
        },
      },
    ],
    porcentaje_maximo: [
      requiredRule,
      percentajeValidator,
      {
        validator: async () => {
          const values = formCalculo.getFieldsValue(['cantidad_minima', 'cantidad_maxima']);
          const cantidad_minima = parseFloat(values.cantidad_minima);
          const cantidad_maxima = parseFloat(values.cantidad_maxima);
          if (values.cantidad_maxima) {
            if (!decimal.test(values.cantidad_maxima)) {
              throw new Error('Ingrese un valor decimal válido');
            } else if (cantidad_maxima > 0 && cantidad_maxima <= 100
              && cantidad_maxima <= cantidad_minima) {
              throw new Error('El campo debe ser mayor al Porcentaje Mínimo');
            }
          }
        },
      },
    ],
    porcentaje_minimo: [
      requiredRule,
      percentajeValidator,
      {
        validator: async () => {
          const values = formCalculo.getFieldsValue(['cantidad_minima', 'cantidad_maxima']);
          const cantidad_minima = parseFloat(values.cantidad_minima);
          const cantidad_maxima = parseFloat(values.cantidad_maxima);
          if (values.cantidad_minima) {
            if (!decimal.test(values.cantidad_minima)) {
              throw new Error('Ingrese un valor decimal válido');
            } else if (cantidad_minima > 0 && cantidad_minima <= 100
              && cantidad_maxima <= cantidad_minima) {
              throw new Error('El campo debe ser menor al Porcentaje Máximo');
            }
          }
        },
      },
    ],
    importe_maximo: [
      requiredRule,
      {
        validator: async () => {
          const values = formCalculo.getFieldsValue(['importe_minimo', 'importe_maximo']);
          const importe_minimo = parseFloat(values.importe_minimo);
          const importe_maximo = parseFloat(values.importe_maximo);
          if (values.importe_maximo) {
            if (!decimal.test(values.importe_maximo)) {
              throw new Error('Ingrese un valor decimal válido');
            } else if (importe_maximo <= importe_minimo) {
              throw new Error('El campo debe ser mayor al Importe Mínimo');
            }
          }
        },
      },
    ],
    importe_minimo: [
      requiredRule,
      {
        validator: async () => {
          const values = formCalculo.getFieldsValue(['importe_minimo', 'importe_maximo']);
          const importe_minimo = parseFloat(values.importe_minimo);
          const importe_maximo = parseFloat(values.importe_maximo);
          if (values.importe_minimo) {
            if (!decimal.test(values.importe_minimo)) {
              throw new Error('Ingrese un valor decimal válido');
            } else if (importe_minimo >= importe_maximo) {
              throw new Error('El campo debe ser menor al Importe Máximo');
            }
          }
        },
      },
    ],
    canales_de_pago: [
      {
        validator: async (rule, val) => {
          if (!val?.length) {
            throw new Error('Seleccione al menos un canal de pago');
          }
        },
      },
    ],
    conditional_required: [
      {
        required: porDiasDeVigencia,
      },
    ],
  };

  // region Render
  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <Table
            cols={columns}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            allowImport
            baseURI={baseURI}
            data={data}
            handleOnRowClick={handleOnRowClick}
            hasAllViewPermission={hasAllViewPermission}
            permission={permission}
            rowKey="id"
            rowSelection={rowSelection}
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tipo de Descuento`}
            extra={(
              <FormSubmitControls
                onFinish={() => onFinish()}
                onCancel={onCancel}
                loading={loading}
                permission={permission}
              />
            )}
            bordered={false}
          >
            <Tabs
              type="card"
              onChange={setCurrentTabKey}
              activeKey={currentTabKey}
            >
              <TabPane
                tab={(
                  <span>
                    General
                  </span>
                )}
                key="general"
                forceRender
              >
                <Form
                  layout="vertical"
                  name="general"
                  form={form}
                  onFinish={() => onFinish()}
                  scrollToFirstError
                  initialValues={{
                    estados_globales: 1,
                    descuento_sobre_base: true,
                    padron: 1,
                  }}
                >
                  <Row gutter={10}>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="clave"
                        label="Clave"
                        rules={rules.required}
                        hasFeedback
                        normalize={capitalize}
                      >
                        <Input allowClear disabled={selectedRowKeys.length} />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="padron"
                        label="Padrón"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select dataSource={padrones} />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="descripcion"
                        label="Descripción"
                        rules={rules.required}
                        hasFeedback
                        normalize={capitalize}
                      >
                        <Input.TextArea allowClear autoSize={{ minRows: 3, maxRows: 5 }} />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="observaciones"
                        label="Observaciones"
                        rules={rules.required}
                        hasFeedback
                        normalize={capitalize}
                      >
                        <Input.TextArea allowClear autoSize={{ minRows: 3, maxRows: 5 }} />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={6}
                    >
                      <Form.Item
                        name="estados_globales"
                        label="Estado"
                        rules={rules.required}
                        hasFeedback
                      >
                        <Select
                          dataSource={estadosGlobales}
                          disabled={!selectedRowKeys.length}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <UnorderedListOutlined />
                    Reglas
                  </span>
                )}
                key="reglas"
                forceRender
              >
                <Form
                  name="reglas"
                  form={formReglas}
                  layout="vertical"
                  onFinish={() => onFinish()}
                  initialValues={{
                    aplica_por_periodo: false,
                    aplicacion_del_descuento: 1,
                    aplicar_a_adicionales: false,
                    es_descuento_especial: false,
                    requiere_autorizacion: false,
                  }}
                >
                  <Row gutter={10}>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="es_descuento_especial"
                        label="Es descuento especial"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select
                          onChange={(especial) => {
                            if (!especial) {
                              const id = formCalculo
                                .getFieldValue('tipo_de_aplicacion');
                              const descripcion = tiposDeAplicacion
                                .find((e) => e.id === id).descripcion.toLowerCase();
                              if (/variable|capturable/.test(descripcion)) {
                                formCalculo.resetFields(['tipo_de_aplicacion']);
                                setTipoApp(1);
                              }
                            }
                          }}
                          trueFalse
                        />
                      </Form.Item>
                    </Col>
                    {es_descuento_especial && (
                      <Col
                        xs={24}
                        sm={24}
                        md={8}
                      >
                        <Form.Item
                          name="requiere_autorizacion"
                          label="Requiere autorización"
                          hasFeedback
                          rules={rules.required}
                        >
                          <Select trueFalse />
                        </Form.Item>
                      </Col>
                    )}
                    {!es_descuento_especial && (
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="aplica_por_periodo"
                        label="Aplicar por periodo"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select trueFalse />
                      </Form.Item>
                    </Col>
                    )}
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="aplicar_a_adicionales"
                        label="Aplicar a adicionales"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select trueFalse />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="aplicacion_del_descuento"
                        label="Aplicación"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select dataSource={tiposDeAplicacionDescuentos} />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <PlainTransfer
                        dataSource={roles}
                        rules={rules.required}
                        label="Roles que pueden aplicar"
                        formItemName="roles_que_pueden_aplicar_descuento"
                        form={formReglas}
                        filterProp="name"
                      />
                      <br />
                    </Col>
                    {requiere_autorizacion && (
                      <Col span={24}>
                        <PlainTransfer
                          dataSource={roles}
                          rules={rules.required}
                          label="Roles que pueden autorizar"
                          formItemName="roles_que_pueden_autorizar_descuentos"
                          form={formReglas}
                          filterProp="name"
                        />
                        <br />
                      </Col>
                    )}
                  </Row>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <CalculatorOutlined />
                    Parámetros de Cálculo
                  </span>
                )}
                key="calculo"
                forceRender
              >
                <Form
                  name="calculo"
                  form={formCalculo}
                  layout="vertical"
                  onFinish={() => onFinish()}
                  initialValues={{
                    tipo_de_aplicacion: 1,
                    el_descuento_es_por_rango_de_fechas: false,
                    es_por_dias_de_vigencia: false,
                    es_por_numero_de_aplicaciones: false,
                  }}
                >
                  <Row gutter={10}>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="tipo_de_aplicacion"
                        label="Tipo de aplicación"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select
                          onChange={setTipoApp}
                          dataSource={tiposDeAplicacion}
                        />
                      </Form.Item>
                    </Col>
                    {tipoApp === 1 && (
                      <Col
                        xs={24}
                        sm={24}
                        md={8}
                      >
                        <NumericInput
                          name="cantidad_minima"
                          label="Porcentaje"
                          rules={rules.porcentaje}
                          decimal
                        />
                      </Col>
                    )}
                    {tipoApp === 2 && (
                      <>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <NumericInput
                            name="cantidad_minima"
                            label="Porcentaje mínimo"
                            rules={rules.porcentaje_minimo}
                            decimal
                          />
                        </Col>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <NumericInput
                            name="cantidad_maxima"
                            label="Porcentaje máximo"
                            rules={rules.porcentaje_maximo}
                            decimal
                          />
                        </Col>
                      </>
                    )}
                    {tipoApp === 3 && (
                      <Col
                        xs={24}
                        sm={24}
                        md={8}
                      >
                        <NumericInput
                          name="importe_minimo"
                          label="Importe"
                          rules={rules.importe}
                          decimal
                        />
                      </Col>
                    )}
                    {tipoApp === 4 && (
                      <>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <NumericInput
                            name="importe_minimo"
                            label="Importe mínimo"
                            rules={rules.importe_minimo}
                            decimal
                          />
                        </Col>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <NumericInput
                            name="importe_maximo"
                            label="Importe máximo"
                            rules={rules.importe_maximo}
                            decimal
                          />
                        </Col>
                      </>
                    )}
                    {tipoApp === 5 && (
                      <Editor
                        rules={rules.required}
                        form={formCalculo}
                        descuento
                        variables={variables}

                      />
                    )}
                    {tipoApp === 6 && (
                      <>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <Form.Item
                            name="catalogo_de_tarifa"
                            rules={rules.required}
                            label="Tarifa"
                            hasFeedback
                          >
                            <Select dataSource={tarifas} />
                          </Form.Item>
                        </Col>
                      </>
                    )}
                    {tipoApp === 7 && (
                    <Editor
                      rules={rules.required}
                      form={formCalculo}
                      descuento
                      variables={variables}
                    />
                    )}
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="el_descuento_es_por_rango_de_fechas"
                        rules={rules.required}
                        label="Por rango de fechas"
                        hasFeedback
                      >
                        <Select onChange={setRequired} trueFalse />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="es_por_dias_de_vigencia"
                        rules={rules.required}
                        label="Días de Vigencia"
                        hasFeedback
                      >
                        <Select onChange={setPorDiasDeVigencia} trueFalse />
                      </Form.Item>
                    </Col>
                    {required && (
                      <Col
                        xs={24}
                        sm={24}
                        md={16}
                      >
                        <Form.Item
                          name="fechas"
                          label="Fecha de inicio"
                          rules={rules.required}
                          hasFeedback
                        >
                          <DatePicker.RangePicker
                            disabledDate={(current) => current && current < Moment().subtract(1, 'days')}
                            format={DATE_FRONTEND_FORMAT}
                            placeholder=""
                          />
                        </Form.Item>
                      </Col>
                    )}
                    {porDiasDeVigencia && (
                      <Col xs={24} sm={24} md={8}>
                        <NumericInput
                          name="dias_de_vigencia"
                          rules={rules.conditional_required}
                          label="Por días de gracia"
                          hasFeedback
                          allowZero
                        />
                      </Col>
                    )}
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        label="Es por número de aplicaciones"
                        name="es_por_numero_de_aplicaciones"
                      >
                        <Select trueFalse />
                      </Form.Item>
                    </Col>
                    {esPorNumeroDeAplicacion && (
                      <Col xs={24} sm={24} md={8}>
                        <NumericInput
                          name="cantidad_de_veces_a_aplicar"
                          label="Cantidad de veces a aplicar"
                        />
                      </Col>
                    )}
                  </Row>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <DollarOutlined />
                    Canales de Pago
                  </span>
                )}
                key="canalesDePago"
                forceRender
              >
                <Form
                  form={formCanalesDePago}
                  layout="vertical"
                  onFinish={() => onFinish()}
                  name="canalesDePago"
                >
                  <Form.Item
                    label=""
                    name="canales_de_pago"
                    rules={rules.canales_de_pago}
                  >
                    <Checkbox.Group style={{ width: '100%' }}>
                      <Row gutter={[10, 5]}>
                        {canalesDePago.map((item) => (
                          <Col
                            xs={24}
                            sm={24}
                            md={8}
                            key={item.id}
                          >
                            <Checkbox value={item.id}>{item.descripcion}</Checkbox>
                          </Col>
                        ))}
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <SettingOutlined />
                    Variables
                  </span>
                )}
                key="variables"
                forceRender
                disabled={tipoApp !== 5}
              >
                <Form
                  form={formVariables}
                  layout="vertical"
                  onFinish={() => onFinish()}
                  name="variables"
                >
                  <Row>
                    <Col span={24}>
                      <PlainTransfer
                        dataSource={variables}
                        label="Variables"
                        formItemName="variables"
                        rules={rules.variables}
                        form={formVariables}
                        filterProp="descripcion_de_variable"
                      />
                    </Col>
                  </Row>
                </Form>
              </TabPane>
            </Tabs>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            setSelectedRowKeys([]);
          }}
          visible={visibleAlert}
          content={`Tipo de Descuento ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default TiposDeDescuentos;
